import { invoke, type InvokeArgs, type InvokeOptions } from '@tauri-apps/api/core';
import type { Platform } from './types';

/**
 * The real implementation. Every call is a Rust command in `src-tauri/src/`,
 * because the whole point of the desktop shell is making requests from outside
 * the webview, where CORS doesn't exist.
 *
 * Commands reject with a plain string; those are rethrown as Errors so callers
 * can treat everything uniformly.
 */
export const tauriPlatform: Platform = {
  fetchUrl: (url) => call('fetch_page', { url }),

  fetchBinary: async (url, referer) => {
    // The command returns raw bytes over Tauri's binary IPC channel — a JSON
    // array of numbers would be an order of magnitude larger and slower.
    const bytes = await call<ArrayBuffer>('fetch_binary', { url, referer });
    // Deliberately untyped: `import/mime.ts` sniffs the real format from the
    // magic bytes, which beats trusting a retail CDN's Content-Type.
    return new Blob([bytes]);
  },

  openExternal: (url) => call('open_external', { url }),

  showDataFolder: () => call('show_data_folder', {}),

  // The archive goes over as a raw IPC body rather than as an argument: a
  // backup is hundreds of megabytes of photos, and Tauri serialises normal
  // arguments as JSON, where a byte costs up to four characters of decimal.
  //
  // The name is framed into the front of that body — two bytes of length,
  // then UTF-8 — rather than sent as an IPC header, which is the obvious way
  // and the way this used to work. A custom header makes the request behind
  // the invoke hang forever on macOS: no dialog, no error, just a promise
  // that never settles. See `save_file` in `src-tauri/src/files.rs`.
  saveFile: async (name, data) => {
    const named = new TextEncoder().encode(name);
    const length = new Uint8Array(2);
    new DataView(length.buffer).setUint16(0, named.length, true);

    return await call<boolean>(
      'save_file',
      await new Blob([length, named, data]).arrayBuffer(),
    );
  },
};

async function call<T>(
  command: string,
  args: InvokeArgs,
  options?: InvokeOptions,
): Promise<T> {
  try {
    return await invoke<T>(command, args, options);
  } catch (thrown) {
    // Rust's `Err(String)` arrives as a bare string. Those strings are written
    // to be read by a person — see `describe()` in net.rs — so they're kept.
    throw typeof thrown === 'string' ? new Error(thrown) : thrown;
  }
}
